import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api_without_cancellation as api } from "../../api/api";
import { formatCalendarDate, setCaseId, setClientId } from "../../Utils/helper";
import FlaggedStaff from "../../features/flaggedCases/FlaggedStaff";
import incidentIcon from "../../assets/images/incident.svg";
import bdayIcon from "../../assets/images/birthdayicon.svg";
import avatarImage from "../../assets/images/avatar.svg";
import ActionBarComponent from "../common/ActionBarComponent";

import "./FlaggedCases.css";

// Matches the height of a flagged case row (four 21px lines plus padding).
const ROW_HEIGHT = 94;

const getFlaggedCasePath = (flagPage) => {
  const clientId = flagPage.for_client?.id;
  const caseId = flagPage.for_case?.id;
  if (!clientId || !caseId) return null;
  const link = flagPage.page_link || flagPage.link || flagPage.for_page?.page_url;
  const fallback = `/bp-home/${clientId}/${caseId}`;
  if (typeof link !== "string" || !link.trim()) return fallback;
  let route;
  try {
    route = new URL(link, window.location.origin).pathname.split("/").filter(Boolean)[0];
  } catch {
    return fallback;
  }
  if (!route) return fallback;
  if (route === "bp-medicalmin" || route === "treatment") return "/treatment/";
  if (route === "search") return "/search/";
  if (route === "bp-firmsetting") return "/bp-firmsetting";
  return `/${route}/${clientId}/${caseId}`;
};

// Heights of the empty striped rows that fill the table down to the bottom of the page.
const getFillerRowHeights = (space) => {
  if (space <= 0) return [];
  const count = Math.max(1, Math.floor(space / ROW_HEIGHT));
  const heights = Array(count).fill(ROW_HEIGHT);
  // The last row takes up whatever is left so the stripes end exactly at the bottom.
  heights[count - 1] = space - ROW_HEIGHT * (count - 1);
  return heights;
};

const FlaggedCases = ({ newFlags, pageObj = {} }) => {
  const navigate = useNavigate();
  const [flaggedPages, setFlaggedPages] = useState([]);
  const [fillerRowHeights, setFillerRowHeights] = useState([]);
  const [openError, setOpenError] = useState("");
  const openingCaseRef = useRef(false);
  const scrollRef = useRef(null);
  const headRef = useRef(null);
  const bodyRef = useRef(null);

  const openFlaggedCase = async (flagPage) => {
    const destination = getFlaggedCasePath(flagPage);
    if (openingCaseRef.current) return;
    if (!destination) {
      setOpenError("This flagged entry is missing its client or case. Refresh and try again.");
      return;
    }
    openingCaseRef.current = true;
    setOpenError("");
    const clientId = flagPage.for_client.id;
    const caseId = flagPage.for_case.id;

    try {
      const pageId =
        destination === `/bp-home/${clientId}/${caseId}`
          ? 35
          : flagPage.for_page?.id || flagPage.for_page || 1;
      await api.get(`/api/switch_client/${clientId}/${caseId}/${pageId}/`);
      setClientId(clientId);
      setCaseId(caseId);
      navigate(destination, {
        state: { openingCase: { ...flagPage.for_case, for_client: flagPage.for_client } },
      });
    } catch (error) {
      console.error("Unable to open flagged case", error);
      setOpenError("Unable to open this case. Please try again.");
    } finally {
      openingCaseRef.current = false;
    }
  };

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    api
      .get("/api/flaggedcases/", { signal: controller.signal })
      .then(({ data }) => {
        if (!active || !Array.isArray(data?.flaggedPages)) return;
        setFlaggedPages(
          data.flaggedPages.filter((flag) => !flag.un_flagged_on && !flag.un_flagged_by)
        );
      })
      .catch((error) => {
        if (active) console.error("flagged cases error", error);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [newFlags]);

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const body = bodyRef.current;
    if (!scroll) return undefined;

    const updateTableLayout = () => {
      // Measure natural name widths before sharing the widest across Assigned To.
      body?.style.removeProperty("--flagged-assignee-name-width");
      const names = Array.from(
        body?.querySelectorAll(".flagged-assignees .person-index-name") ?? []
      );
      const widestName = names.reduce(
        (widest, name) => Math.max(widest, name.getBoundingClientRect().width),
        0
      );
      if (names.length) {
        body.style.setProperty("--flagged-assignee-name-width", `${Math.ceil(widestName)}px`);
      }

      const caseRowsHeight = Array.from(body?.querySelectorAll("tr[data-id]") ?? []).reduce(
        (total, row) => total + row.offsetHeight,
        0
      );
      const space = scroll.clientHeight - (headRef.current?.offsetHeight ?? 0) - caseRowsHeight;
      setFillerRowHeights(getFillerRowHeights(space));
    };

    updateTableLayout();
    const resizeObserver = new ResizeObserver(updateTableLayout);
    resizeObserver.observe(scroll);
    return () => resizeObserver.disconnect();
  }, [flaggedPages]);

  return (
    <div className="main-content hidden-scrollbar flagged-ML-5 flagged-cases">
      <ActionBarComponent
        src="/BP_resources/images/icon/flagged-cases-logo-icon.svg"
        page_name={pageObj?.name || "Flagged Cases"}
        pageObj={pageObj}
        isChecklist={pageObj?.PageChklst || false}
      />
      {openError && (
        <div className="alert alert-danger m-0" role="alert">
          {openError}
        </div>
      )}
      <div className="section__content section__content--p30 m-t-5">
        <div className="container-fluid px-0">
          <div className="col-lg-12 top-head">
            <div
              ref={scrollRef}
              className="table-responsive table--no-card rounded-0 flagged-cases-scroll"
            >
              <table
                className="table table-borderless table-striped has-height-25 table-earning fixed-table-header-2 flagged-cases-table"
                id="flagged-cases-table"
              >
                <thead ref={headRef}>
                  <tr>
                    <th scope="col" className="flagged-row-number">
                      <span className="sr-only">Number</span>
                    </th>
                    <th scope="col">CASE</th>
                    <th scope="col">PAGE</th>
                    <th className="flagged-spacer" aria-hidden="true" />
                    <th scope="col">FLAGGED BY</th>
                    <th scope="col">REASON</th>
                    <th className="flagged-spacer" aria-hidden="true" />
                    <th scope="col">ASSIGNED TO</th>
                  </tr>
                </thead>
                <tbody id="flagged_pages_table" ref={bodyRef}>
                  {flaggedPages.map((flagPage, index) => {
                    const destination = getFlaggedCasePath(flagPage);
                    return (
                      <tr
                        data-id={flagPage.id}
                        key={flagPage.id}
                        className={destination ? "flagged-case-row" : undefined}
                        onClick={(event) => {
                          if (
                            event.target.closest(
                              "a, button, input, select, textarea, [role='button']"
                            )
                          )
                            return;
                          openFlaggedCase(flagPage);
                        }}
                      >
                        <td className="flagged-row-number">{index + 1}</td>
                        <td className="flagged-case-identity">
                          <div className="d-flex align-items-center gap-1 client-name-box font-600">
                            <img
                              src={flagPage.for_client?.profile_pic_19p || avatarImage}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = avatarImage;
                              }}
                              className=""
                              alt=""
                              width={15}
                              height={15}
                              style={{
                                background: "var(--primary-30)",
                                borderRadius: "50%",
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                            />
                            {destination ? (
                              <Link
                                className="flagged-case-link"
                                to={destination}
                                onClick={(event) => {
                                  if (
                                    event.button !== 0 ||
                                    event.ctrlKey ||
                                    event.metaKey ||
                                    event.shiftKey ||
                                    event.altKey
                                  )
                                    return;
                                  event.preventDefault();
                                  openFlaggedCase(flagPage);
                                }}
                              >
                                {[flagPage.for_client?.last_name, flagPage.for_client?.first_name]
                                  .filter(Boolean)
                                  .join(", ")}
                              </Link>
                            ) : (
                              <span>
                                {[flagPage.for_client?.last_name, flagPage.for_client?.first_name]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <img src={bdayIcon} className="" alt="" width={15} height={15} />
                            <span>{formatCalendarDate(flagPage.for_client?.birthday)}</span>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            {flagPage.for_case?.case_type?.casetype_icon && (
                              <img
                                src={flagPage.for_case.case_type.casetype_icon}
                                className=""
                                alt=""
                                width={15}
                                height={15}
                              />
                            )}
                            <span>{flagPage.for_case?.case_type?.name || ""}</span>
                          </div>
                          <div className="d-flex gap-1 align-items-center">
                            <img src={incidentIcon} className="" alt="" width={15} height={15} />
                            <span>{formatCalendarDate(flagPage.for_case?.incident_date)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1 justify-content-center">
                            {flagPage.page_icon && (
                              <img
                                src={flagPage.page_icon}
                                className=""
                                alt=""
                                width={15}
                                height={15}
                              />
                            )}
                            <span>{flagPage.page_name || ""}</span>
                          </div>
                        </td>
                        <td className="flagged-spacer" aria-hidden="true" />
                        <td className="text-center">
                          <div className="d-flex flex-column align-items-center gap-1">
                            <div className="flagged-timestamp">
                              {flagPage.flagged_on && (
                                <>
                                  {new Date(flagPage.flagged_on).toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  })}{" "}
                                  <span className="text-grey">at</span>{" "}
                                  {new Date(flagPage.flagged_on)
                                    .toLocaleTimeString("en-US", {
                                      hour: "numeric",
                                      minute: "2-digit",
                                      hour12: true,
                                    })
                                    .toLowerCase()}{" "}
                                </>
                              )}
                              <span className="text-grey">by:</span>
                            </div>
                            <FlaggedStaff
                              staff={{
                                user_id: flagPage.flagged_by,
                                first_name: flagPage.flagged_by_first_name,
                                last_name: flagPage.flagged_by_last_name,
                                profile_pic_url: flagPage.flagged_by_profile_pic_url,
                              }}
                            />
                          </div>
                        </td>
                        <td className="flagged-reason">
                          {flagPage.flagged_reason || "No reason provided."}
                        </td>
                        <td className="flagged-spacer" aria-hidden="true" />
                        <td className="flagged-assigned-to">
                          {flagPage.assigned_staff?.length ? (
                            <div className="flagged-assignees">
                              {[...flagPage.assigned_staff]
                                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                                .map((staff) => (
                                  <div
                                    className="flagged-assignee"
                                    key={`${staff.position}-${staff.id}`}
                                  >
                                    <span className="flagged-assignee-role">
                                      {staff.role?.name || ""}
                                    </span>
                                    <FlaggedStaff staff={staff} />
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <span className="text-muted">No assigned reviewers</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {fillerRowHeights.map((height, index) => (
                    <tr key={`filler-${index}`} aria-hidden="true" style={{ height }}>
                      <td colSpan={8} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlaggedCases;
